import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import { grey } from '@mui/material/colors';
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Settings } from '@mui/icons-material';
import React from 'react';
import OptionTabPanel from './OptionTabPanel';
import Analytics from './Analytics';
import AllLogosPicker from './Settings/AllLogosPicker';
import ColorSchemePicker from './Settings/ColorSchemePicker';
import DefaultAddPicker from './Settings/DefaultAddPicker';
import DomainWizard from './Settings/DomainWizard';
import AdvancedOptions from './Settings/Integrations';
import MainOptions from './Settings/MainOptions';
import OnOffSwitch from './Settings/OnOffSwitch';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import BlogChecklist from './BlogChecklist';
import Plan from './Plan';
import { useSnackbar } from 'notistack';
import BasicBlogButtons from './BasicBlogButtons';
import AddDomainWizard from './Settings/AddDomainWizard';

dayjs.extend(relativeTime);

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const useStyles = makeStyles()(theme => ({
  card: {
    backgroundColor: theme.palette.primary.main,
  },
}));

const whiteTheme = createTheme({
  palette: {
    primary: {
      main: grey[50],
    },
  },
});

export default function BlogSettings(props) {
  const [value, setValue] = React.useState(props.page || 0);
  const [openAddDomainWizard, setOpenAddDomainWizard] = React.useState(false);

  const { isBuilding, buildProgress, blogId, editKey, blog } = props;
  const { plan, expiryDate, domain, isCancelled } = blog;

  const handleChange = (_, newValue) => {
    setValue(newValue);
  };

  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  return (
    <>
      <div>
        <div className="md:tw-flex md:tw-gap-12 mb-2">
          <div>
            <span className="tw-font-medium">Plan</span>:{' '}
            {new Date(expiryDate) < new Date() ? (
              <span className="tw-text-red-600 dark:tw-text-red-400">
                Expired
              </span>
            ) : (
              <>
                {plan}
                {isCancelled || plan === 'trial' ? (
                  <div
                    className={
                      isCancelled ||
                      dayjs(expiryDate).subtract(3, 'day').toDate() <
                        dayjs().toDate()
                        ? 'tw-text-red-600 dark:tw-text-red-400'
                        : undefined
                    }
                  >
                    Ends {dayjs(expiryDate).fromNow()}
                  </div>
                ) : (
                  ''
                )}
              </>
            )}
          </div>
          <div>
            Domain:{' '}
            {domain ? (
              <a
                href={`${domain}/api/preview`}
                target="_blank"
                rel="nofollow noreferrer noopener"
                className="hover:tw-text-primary-500 dark:hover:tw-text-primary-300 hover:tw-underline"
              >
                {domain}
              </a>
            ) : (
              <>
                <a
                  href={`https://${blogId}.truvvl.blog/api/preview`}
                  target="_blank"
                  rel="nofollow noreferrer noopener"
                  className="hover:tw-text-primary-500 dark:hover:tw-text-primary-300 hover:tw-underline"
                >
                  https://{blogId}.truvvl.blog/
                </a>

                <div>
                  {new Date(expiryDate) < new Date() ? (
                    <></>
                  ) : plan === 'trial' ? (
                    <span
                      onClick={() => {
                        setValue(4);
                        enqueueSnackbar(
                          'Custom domains are only available in our paid plans',
                          {
                            variant: 'info',
                          },
                        );
                      }}
                      className="tw-cursor-pointer tw-text-primary-500 dark:tw-text-primary-300 hover:tw-underline"
                    >
                      Connect your own domain
                    </span>
                  ) : (
                    <span
                      onClick={() => {
                        setOpenAddDomainWizard(true);
                      }}
                      className="tw-cursor-pointer tw-text-primary-500 dark:tw-text-primary-300 hover:tw-underline"
                    >
                      Connect your own domain
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="text-right tw-grow md:tw-block tw-hidden">
            <a
              href={`https://${blogId}.truvvl.blog/api/edit?editKey=${editKey}`}
              target="_blank"
              rel="nofollow noreferrer noopener"
            >
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={isBuilding && buildProgress < 100}
              >
                Open Site Builder <Settings className="ml-2" />
              </Button>
            </a>
          </div>
        </div>
        <div className="tabCard">
          <Card className={classes.card}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={whiteTheme}>
                {new Date(expiryDate) > new Date() && (
                  <Tabs
                    value={value}
                    onChange={handleChange}
                    scrollButtons
                    variant="scrollable"
                    indicatorColor="primary"
                    textColor="primary"
                    allowScrollButtonsMobile
                  >
                    <Tab
                      label="Checklist"
                      {...a11yProps(0)}
                      onClick={() => {
                        history.pushState(null, '', '/dashboard/hosting');
                      }}
                    />
                    <Tab
                      label="General"
                      {...a11yProps(1)}
                      onClick={() => {
                        history.pushState(
                          null,
                          '',
                          '/dashboard/hosting/general',
                        );
                      }}
                    />
                    <Tab
                      label="Appearance"
                      {...a11yProps(2)}
                      onClick={() => {
                        history.pushState(
                          null,
                          '',
                          '/dashboard/hosting/appearance',
                        );
                      }}
                    />
                    <Tab
                      label="Integrations"
                      {...a11yProps(3)}
                      onClick={() => {
                        history.pushState(
                          null,
                          '',
                          '/dashboard/hosting/integrations',
                        );
                      }}
                    />
                    <Tab
                      label="Plan"
                      {...a11yProps(4)}
                      onClick={() => {
                        history.pushState(null, '', '/dashboard/hosting/plan');
                      }}
                    />
                    <Tab
                      label="Analytics"
                      {...a11yProps(5)}
                      onClick={() => {
                        history.pushState(
                          null,
                          '',
                          '/dashboard/hosting/analytics',
                        );
                      }}
                    />
                  </Tabs>
                )}
              </ThemeProvider>
            </StyledEngineProvider>
          </Card>
        </div>
        {new Date(expiryDate) > new Date() ? (
          <>
            <OptionTabPanel value={value} index={0}>
              <BlogChecklist {...props} setTab={setValue} plan={plan} />
            </OptionTabPanel>
            <OptionTabPanel value={value} index={1}>
              <MainOptions {...props} isAdvanced />
              <div className="mt-5" />
              <DefaultAddPicker {...props} />
              <DomainWizard {...props} setValue={setValue} />
              <BasicBlogButtons {...props} />
            </OptionTabPanel>
            <OptionTabPanel value={value} index={2}>
              <ColorSchemePicker {...props} />
              <div className="mt-5" />
              <OnOffSwitch
                value={props.defaultDarkMode}
                setValue={props.setDefaultDarkMode}
                title="Default to dark mode?"
                description="If you enable this, your blog will always load in dark mode."
                label="Default to dark mode"
              />
              <AllLogosPicker {...props} />
              <BasicBlogButtons {...props} />
            </OptionTabPanel>
            <OptionTabPanel value={value} index={3}>
              <AdvancedOptions {...props} />
              <BasicBlogButtons {...props} />
            </OptionTabPanel>
            <OptionTabPanel value={value} index={4}>
              <Plan {...props} />
            </OptionTabPanel>
            <OptionTabPanel value={value} index={5}>
              {props.blog.plan === 'basic' ? (
                'You need to upgrade your plan to Pro or VIP for built-in analytics.'
              ) : (
                <Analytics {...props} />
              )}
            </OptionTabPanel>
          </>
        ) : (
          <Plan {...props} />
        )}
        {/* <OptionTabPanel value={value} index={5}>
        https://aaa.truvvl.blog is your blog&apos;s address. Connect your own
        subdomain or domain:
      </OptionTabPanel> */}
      </div>
      <AddDomainWizard
        {...props}
        open={openAddDomainWizard}
        setOpen={setOpenAddDomainWizard}
      />
      <style jsx global>{`
        .tabCard {
          max-width: calc(100vw - 60px);
        }
        @media (min-width: 769px) {
          .tabCard {
            max-width: calc(100vw - 120px);
          }
        }
      `}</style>
    </>
  );
}
